import { useRef, useEffect, useCallback, useState, useMemo } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import type { UIMessage } from "ai";
import { Button } from "~/components/ui/button";
import { Textarea } from "~/components/ui/textarea";
import { CaraMessageBubble, parseSuggestions, type ParsedSuggestions } from "./CaraMessageBubble";
import type { TimelineAsset } from "./CaraAssetTimeline";
import { useT } from "~/i18n/context";

function playCompletionChime() {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.08, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
    osc.onended = () => ctx.close();
  } catch {}
}

function extractSketchDataUrls(messages: UIMessage[]): string[] {
  const urls: string[] = [];
  for (const msg of messages) {
    if (msg.role !== "user") continue;
    for (const part of msg.parts ?? []) {
      if (
        part.type === "file" &&
        "url" in part &&
        typeof (part as { url?: string }).url === "string"
      ) {
        const filePart = part as { url: string; mediaType?: string };
        if (filePart.mediaType?.startsWith("image/") || filePart.url.startsWith("data:image/")) {
          urls.push(filePart.url);
        }
      }
    }
  }
  return urls;
}

type ToolPart = {
  type: string;
  toolCallId?: string;
  toolName?: string;
  state?: string;
  output?: unknown;
  [key: string]: unknown;
};

type ToolLabels = Record<string, string>;

function extractTimelineAssets(messages: UIMessage[], toolLabels: ToolLabels): TimelineAsset[] {
  const assets: TimelineAsset[] = [];
  let counter = 0;

  for (let mi = 0; mi < messages.length; mi++) {
    const msg = messages[mi];
    if (msg.role !== "assistant") continue;

    for (const part of msg.parts ?? []) {
      const p = part as ToolPart;
      if (!p.type?.startsWith("tool-") && p.type !== "dynamic-tool") continue;
      if ((p.state !== "result" && p.state !== "output-available") || !p.output) continue;

      const toolName = p.toolName ?? (p.type.startsWith("tool-") ? p.type.slice(5) : "unknown");
      const output = p.output as Record<string, unknown>;
      if (output.status === "error" || output.status === "skipped") continue;

      assets.push({
        id: p.toolCallId ?? `asset-${counter}`,
        type: toolName === "calculate_bom" || toolName === "finalize_design"
          ? toolName
          : (output.dataUrl ? "render" : toolName === "analyze_sketch" ? "analyze_sketch" : toolName),
        toolName,
        label: toolLabels[toolName] ?? toolName,
        dataUrl: typeof output.dataUrl === "string" ? output.dataUrl : undefined,
        videoUrl: typeof output.videoUrl === "string" ? output.videoUrl : undefined,
        videoBase64: typeof output.videoBase64 === "string" ? output.videoBase64 : undefined,
        mediaType: typeof output.mediaType === "string" ? output.mediaType : undefined,
        messageIndex: mi,
        data: output,
      });
      counter++;
    }
  }

  return assets;
}

function getPlaceholder(messageCount: number, p: { initial: string; direction: string; adjust: string; finalize: string }): string {
  if (messageCount === 0) return p.initial;
  if (messageCount < 6) return p.direction;
  if (messageCount < 12) return p.adjust;
  return p.finalize;
}

export function CaraChatPanel({
  sessionId,
  onNewArtifact,
  onAssetsChange,
  apiUrl = "/resources/cara",
  mode = "designer",
  initialPrompt,
  creatorName,
  scrollContainerRef,
  restoredUIMessages,
  restoredArtifacts,
}: {
  sessionId: string | null;
  /** Reserved for future auto-session creation on first message */
  onSessionCreated?: (id: string) => void;
  onNewArtifact?: () => void;
  onAssetsChange?: (assets: TimelineAsset[]) => void;
  /** Base URL for the Cara streaming resource route. */
  apiUrl?: string;
  /** Audience mode — affects welcome copy and suggestion starters. */
  mode?: "designer" | "buyer";
  /** Auto-sent as the first message when the chat mounts with no messages. */
  initialPrompt?: string;
  /** Creator name sent with each request for demo gallery attribution. */
  creatorName?: string;
  /** External ref to the scroll container, used for scroll-to-message from timeline */
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
  /** Full UIMessage[] from the server for session restoration (includes tool parts). */
  restoredUIMessages?: unknown[];
  /** Pre-loaded artifacts from the server for session restoration (populates timeline). */
  restoredArtifacts?: Array<{ id: string; type: string; data: Record<string, unknown>; imageUrl?: string; createdAt: string }>;
}) {
  const { t, locale } = useT();
  const c = useMemo(() => {
    const base = t.caraChat;
    if (mode !== "buyer" || !base.buyerOverrides) return base;
    const bo = base.buyerOverrides;
    return {
      ...base,
      empty: { ...base.empty, ...bo.empty },
      placeholder: { ...base.placeholder, ...bo.placeholder },
      starters: bo.starters ?? base.starters,
    };
  }, [t, mode]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [imagePreview, setImagePreview] = useState<{ dataUrl: string; file: File } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showScrollPill, setShowScrollPill] = useState(false);
  const isNearBottomRef = useRef(true);

  const [inputValue, setInputValue] = useState("");

  const initialMessages = useMemo<UIMessage[] | undefined>(() => {
    if (!restoredUIMessages?.length) return undefined;
    return restoredUIMessages as UIMessage[];
  }, [restoredUIMessages]);

  const demoAudience = mode === "buyer" ? "brands" : undefined;
  const transport = useMemo(
    () => new DefaultChatTransport({
      api: apiUrl,
      body: { sessionId, creatorName: creatorName || undefined, locale, demoAudience },
      prepareSendMessagesRequest: ({ messages: msgs }) => {
        const lastIdx = msgs.length - 1;
        const lite = msgs.map((m, i) => {
          if (i === lastIdx) return m;
          const hasLargeData = m.parts.some(
            (p) => p.type === "file" && "url" in p && typeof (p as { url?: string }).url === "string" && ((p as { url: string }).url).length > 1000,
          );
          if (!hasLargeData) return m;
          return {
            ...m,
            parts: m.parts.filter((p) => p.type !== "file"),
          };
        });
        return { body: { messages: lite, sessionId, creatorName: creatorName || undefined, locale, demoAudience } };
      },
    }),
    [sessionId, apiUrl, creatorName, locale, demoAudience],
  );

  const { messages, sendMessage, status, error, stop, setMessages } = useChat({
    transport,
    id: sessionId || undefined,
    messages: initialMessages,
    onFinish: () => {
      onNewArtifact?.();
      if (document.hidden) playCompletionChime();
    },
  });

  const isStreaming = status === "streaming";
  const isSubmitted = status === "submitted";
  const isWorking = isStreaming || isSubmitted;
  const sketchDataUrls = useMemo(() => extractSketchDataUrls(messages), [messages]);

  const activeSuggestions = useMemo<ParsedSuggestions | null>(() => {
    if (isStreaming) return null;
    const lastMsg = messages.at(-1);
    if (!lastMsg || lastMsg.role !== "assistant") return null;
    const lastTextPart = [...lastMsg.parts].reverse().find(
      (p) => p.type === "text" && "text" in p,
    );
    if (!lastTextPart || lastTextPart.type !== "text") return null;
    const { suggestions } = parseSuggestions(
      (lastTextPart as { type: "text"; text: string }).text,
    );
    return suggestions;
  }, [messages, isStreaming]);

  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customValue, setCustomValue] = useState("");
  const customInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setShowCustomInput(false);
    setCustomValue("");
  }, [messages.length]);

  useEffect(() => {
    if (showCustomInput) customInputRef.current?.focus();
  }, [showCustomInput]);

  const handleSuggestionClick = useCallback(
    (option: string) => {
      sendMessage({ text: option });
    },
    [sendMessage],
  );

  const handleCustomSubmit = useCallback(() => {
    const val = customValue.trim();
    if (!val) return;
    setShowCustomInput(false);
    setCustomValue("");
    sendMessage({ text: val });
  }, [customValue, sendMessage]);

  const toolLabels = c.toolLabels as ToolLabels;

  const timelineAssets = useMemo(() => {
    const live = extractTimelineAssets(messages, toolLabels);
    if (!restoredArtifacts?.length) return live;

    const restored = restoredArtifacts.map((a, i): TimelineAsset => ({
      id: a.id,
      type: a.type,
      toolName: a.type,
      label: toolLabels[a.type] ?? a.type,
      dataUrl: a.imageUrl,
      messageIndex: i,
      data: a.data,
    }));

    if (live.length === 0) return restored;

    const liveIds = new Set(live.map((a) => a.id));
    const restoredOnly = restored.filter((a) => !liveIds.has(a.id));
    return [...restoredOnly, ...live];
  }, [messages, restoredArtifacts, toolLabels]);
  const prevAssetIdsRef = useRef("");
  useEffect(() => {
    const key = timelineAssets.map((a) => a.id).join(",");
    if (key !== prevAssetIdsRef.current) {
      prevAssetIdsRef.current = key;
      onAssetsChange?.(timelineAssets);
    }
  }, [timelineAssets, onAssetsChange]);

  // Scroll management
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
      isNearBottomRef.current = nearBottom;
      setShowScrollPill(!nearBottom && messages.length > 0);
    };
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [messages.length]);

  useEffect(() => {
    if (isNearBottomRef.current && scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages, isStreaming]);

  const didSendInitialRef = useRef(false);
  useEffect(() => {
    if (initialPrompt && !didSendInitialRef.current && messages.length === 0 && status === "ready") {
      didSendInitialRef.current = true;
      sendMessage({ text: initialPrompt });
    }
  }, [initialPrompt, messages.length, status, sendMessage]);

  useEffect(() => {
    textareaRef.current?.focus();
  }, [sessionId]);

  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (imagePreview) setImagePreview(null);
        else if (isWorking) stop();
      }
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [imagePreview, isWorking, stop]);

  const handleSend = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      const text = inputValue.trim();
      if (!text && !imagePreview) return;

      if (imagePreview) {
        const files = [{
          type: "file" as const,
          mediaType: imagePreview.file.type || "image/png",
          url: imagePreview.dataUrl,
        }];
        const messageText = text || c.sketchFallback;
        setImagePreview(null);
        setInputValue("");
        sendMessage({ text: messageText, files });
      } else {
        setInputValue("");
        sendMessage({ text });
      }
      if (textareaRef.current) textareaRef.current.style.height = "auto";
    },
    [inputValue, imagePreview, sendMessage, c.sketchFallback],
  );

  const autoResize = useCallback(() => {
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    });
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleImageSelect = useCallback((file: File) => {
    if (!file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview({ dataUrl: reader.result as string, file });
    };
    reader.readAsDataURL(file);
  }, []);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleImageSelect(file);
      e.target.value = "";
    },
    [handleImageSelect],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) handleImageSelect(file);
          break;
        }
      }
    },
    [handleImageSelect],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleImageSelect(file);
    },
    [handleImageSelect],
  );

  const scrollToBottom = useCallback(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    setShowScrollPill(false);
  }, []);

  const handleEditMessage = useCallback((msgIndex: number, newText: string) => {
    const truncated = messages.slice(0, msgIndex);
    setMessages(truncated);
    sendMessage({ text: newText });
  }, [messages, setMessages, sendMessage]);

  const handleRegenerate = useCallback(() => {
    const lastUserMsg = [...messages].reverse().find((m) => m.role === "user");
    if (!lastUserMsg) return;
    const textPart = lastUserMsg.parts.find((p) => p.type === "text" && "text" in p);
    const text = textPart ? (textPart as { type: "text"; text: string }).text : "";
    const fileParts = lastUserMsg.parts
      .filter((p) => p.type === "file" && "url" in p)
      .map((p) => {
        const fp = p as { type: "file"; url: string; mediaType?: string };
        return { type: "file" as const, url: fp.url, mediaType: fp.mediaType || "image/png" };
      });
    const truncated = messages.slice(0, messages.indexOf(lastUserMsg));
    setMessages(truncated);
    if (fileParts.length > 0) {
      sendMessage({ text: text || c.sketchFallback, files: fileParts });
    } else {
      sendMessage({ text });
    }
  }, [messages, sendMessage, setMessages, c.sketchFallback]);

  const activeToolName = useMemo(() => {
    if (!isStreaming) return null;
    const lastMsg = messages.at(-1);
    if (!lastMsg || lastMsg.role !== "assistant") return null;
    for (let i = lastMsg.parts.length - 1; i >= 0; i--) {
      const p = lastMsg.parts[i] as ToolPart;
      if ((p.type?.startsWith("tool-") || p.type === "dynamic-tool") && p.state !== "result" && p.state !== "output-available") {
        const name = p.toolName ?? (p.type.startsWith("tool-") ? p.type.slice(5) : "unknown");
        return c.toolLabels[name as keyof typeof c.toolLabels] ?? name;
      }
    }
    return null;
  }, [isStreaming, messages]);

  return (
    <div
      className="flex flex-col h-full relative"
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-primary/5 border-2 border-dashed border-primary/50 rounded-xl backdrop-blur-[1px]">
          <p className="text-sm text-primary font-medium">{c.dragOverlay}</p>
        </div>
      )}

      {/* Messages */}
      <div
        ref={(el) => {
          (scrollRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
          if (scrollContainerRef) (scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        }}
        className="flex-1 overflow-y-auto px-3 sm:px-5 lg:px-8 py-5 sm:py-8 space-y-5 sm:space-y-6 cara-scroll"
        role="log"
        aria-label="Design conversation with Cara"
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-10 w-10 sm:h-12 sm:w-12 text-primary/20 mb-4 sm:mb-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z" />
            </svg>
            <p className="text-base sm:text-lg font-display text-foreground mb-2">{c.empty.title}</p>
            <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
              {c.empty.description}
            </p>
            <div className="mt-4 sm:mt-6 flex flex-wrap gap-1.5 sm:gap-2 justify-center px-2 sm:px-0">
              {c.starters.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => sendMessage({ text: s.text })}
                  className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] text-muted-foreground border border-border/50 rounded-full px-3 sm:px-3.5 py-1.5 sm:py-2 hover:bg-teal/5 hover:border-teal/30 hover:text-teal active:scale-[0.97] transition-all duration-150 cursor-pointer"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, i) => (
            <CaraMessageBubble
              key={msg.id}
              message={msg}
              index={i}
              isStreaming={isWorking && i === messages.length - 1}
              sessionId={sessionId}
              sketchDataUrls={sketchDataUrls}
              mode={mode}
              onEdit={msg.role === "user" ? handleEditMessage : undefined}
            />
          ))
        )}

        {/* Suggestion chips */}
        {activeSuggestions && !isWorking && (
          <div className="cara-msg-enter flex justify-start pl-8 sm:pl-9" style={{ animationDelay: "80ms" }}>
            <div className="flex flex-wrap gap-2 sm:gap-2.5 max-w-full sm:max-w-[85%]">
              {activeSuggestions.options.map((option, optIdx) => {
                const isPrimary = optIdx === 0;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleSuggestionClick(option)}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium active:scale-[0.97] transition-all duration-150 ${
                      isPrimary
                        ? "text-white hover:brightness-110"
                        : "border border-teal/25 bg-teal/[0.04] text-teal hover:bg-teal/10 hover:border-teal/40"
                    }`}
                    style={isPrimary ? { background: "var(--teal-sapphire)", color: "var(--teal-sapphire-fg, #fff)" } : undefined}
                  >
                    {isPrimary && (
                      <svg viewBox="0 0 16 16" fill="currentColor" className="h-3 w-3 opacity-80">
                        <path d="M8 2a.75.75 0 0 1 .75.75v4.5h4.5a.75.75 0 0 1 0 1.5h-4.5v4.5a.75.75 0 0 1-1.5 0v-4.5h-4.5a.75.75 0 0 1 0-1.5h4.5v-4.5A.75.75 0 0 1 8 2Z" />
                      </svg>
                    )}
                    {option}
                  </button>
                );
              })}
              {activeSuggestions.allowCustom && !showCustomInput && (
                <button
                  type="button"
                  onClick={() => setShowCustomInput(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border/50 bg-card/50 px-3.5 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm text-muted-foreground hover:bg-muted hover:border-border active:scale-[0.97] transition-all duration-150"
                >
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-3.5 w-3.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 3.5 8 9l-5.5-5.5" />
                    <path strokeLinecap="round" d="M2 12h12" />
                  </svg>
                  {c.suggestions?.custom ?? "Other…"}
                </button>
              )}
              {showCustomInput && (
                <div className="flex items-center gap-2 w-full mt-1">
                  <input
                    ref={customInputRef}
                    type="text"
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCustomSubmit();
                      if (e.key === "Escape") { setShowCustomInput(false); setCustomValue(""); }
                    }}
                    placeholder={c.suggestions?.customPlaceholder ?? "Type your answer…"}
                    className="flex-1 min-w-0 rounded-lg border border-border bg-background px-3.5 py-2 text-xs sm:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-teal/40"
                  />
                  <button
                    type="button"
                    onClick={handleCustomSubmit}
                    disabled={!customValue.trim()}
                    className="shrink-0 rounded-lg px-4 py-2 text-xs sm:text-sm text-white hover:brightness-110 disabled:opacity-40 transition-all"
                    style={{ background: "var(--teal-sapphire)" }}
                  >
                    {c.suggestions?.send ?? "Send"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Regenerate button after last assistant message */}
        {!isWorking && messages.length >= 2 && messages.at(-1)?.role === "assistant" && !activeSuggestions && (
          <div className="flex justify-start pl-8 sm:pl-9">
            <button
              type="button"
              onClick={handleRegenerate}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-3.5 w-3.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 8A5.5 5.5 0 0 1 3.08 11.5M2.5 8a5.5 5.5 0 0 1 10.42-2.5" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 3v2.5H11M2.5 13v-2.5H5" />
              </svg>
              {c.regenerate ?? "Regenerate"}
            </button>
          </div>
        )}

        {/* Thinking indicator — shown between send and first assistant content */}
        {isWorking && messages.length > 0 && messages[messages.length - 1]?.role === "user" && (
          <div className="cara-msg-enter flex justify-start">
            <div className="flex items-center gap-3 pl-8 sm:pl-9">
              <div className="absolute left-3 sm:left-5 lg:left-8">
                <div className="h-5 w-5 sm:h-6 sm:w-6 rounded-full flex items-center justify-center" style={{ background: "oklch(0.45 0.08 213 / 0.1)" }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-2.5 w-2.5 sm:h-3 sm:w-3" style={{ color: "var(--teal-sapphire)" }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" />
                  </svg>
                </div>
              </div>
              <div className="flex items-center gap-1.5" aria-label="Cara is thinking">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="h-1.5 w-1.5 rounded-full"
                    style={{
                      background: "var(--teal-sapphire)",
                      opacity: 0.7,
                      animation: "cara-think 1.4s ease-in-out infinite",
                      animationDelay: `${i * 200}ms`,
                    }}
                  />
                ))}
              </div>
              <span className="text-xs text-muted-foreground/50 font-medium">
                {isSubmitted ? "Thinking…" : "Composing…"}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Scroll to bottom pill */}
      {showScrollPill && (
        <button
          onClick={scrollToBottom}
          className="absolute bottom-20 left-1/2 -translate-x-1/2 z-10 rounded-full bg-card border border-border px-3 py-1 text-xs text-muted-foreground shadow-md hover:bg-muted transition-colors"
        >
          {c.scrollPill}
        </button>
      )}

      {/* Error */}
      {error && (
        <div className="mx-3 sm:mx-5 lg:mx-8 mb-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 sm:px-4 py-2.5 sm:py-3 text-sm text-destructive">
          {(() => {
            const raw = error.message ?? "";
            let parsed = raw;
            try { const j = JSON.parse(raw); if (j?.error) parsed = j.error; } catch {}
            if (parsed.includes("Studio Credits")) return parsed;
            if (parsed.includes("rate limit") || parsed.includes("too quickly") || parsed.includes("Daily demo")) return parsed;
            return c.error;
          })()}
        </div>
      )}

      {/* Tool activity status bar */}
      {isStreaming && activeToolName && (
        <div className="mx-3 sm:mx-5 lg:mx-8 mb-0 py-2 flex items-center gap-2.5 text-xs text-muted-foreground/70 min-w-0">
          <span className="h-1.5 w-1.5 rounded-full animate-pulse shrink-0" style={{ background: "var(--teal-sapphire)" }} />
          <span className="truncate">Working on <span className="text-foreground/80 font-medium">{activeToolName}</span></span>
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-border/60 p-2.5 sm:p-3 px-3 sm:px-5 lg:px-8 bg-background">
        {/* Image preview */}
        {imagePreview && (
          <div className="mb-2 flex items-center gap-2">
            <div className="relative">
              <img
                src={imagePreview.dataUrl}
                alt="Upload preview"
                className="h-16 w-16 rounded-lg object-cover border border-border"
              />
              <button
                onClick={() => setImagePreview(null)}
                className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center text-[10px]"
                aria-label="Remove image"
              >
                ×
              </button>
            </div>
            <p className="text-xs text-muted-foreground">{imagePreview.file.name}</p>
          </div>
        )}

        <form onSubmit={handleSend} className="flex items-end gap-2">
          {/* Upload button */}
          <label className="shrink-0">
            <input
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={handleFileInput}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="cursor-pointer"
              aria-label="Upload inspiration image"
              asChild
            >
              <span>
                <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                  <path fillRule="evenodd" d="M1 5.25A2.25 2.25 0 0 1 3.25 3h13.5A2.25 2.25 0 0 1 19 5.25v9.5A2.25 2.25 0 0 1 16.75 17H3.25A2.25 2.25 0 0 1 1 14.75v-9.5Zm1.5 5.81V14.75c0 .414.336.75.75.75h13.5a.75.75 0 0 0 .75-.75v-2.06l-2.22-2.22a.75.75 0 0 0-1.06 0L9.06 13.06a.75.75 0 0 1-1.06 0l-1.22-1.22a.75.75 0 0 0-1.06 0L2.5 11.06ZM12 7a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" clipRule="evenodd" />
                </svg>
              </span>
            </Button>
          </label>

          {/* Textarea */}
          <Textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => { setInputValue(e.target.value); autoResize(); }}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={getPlaceholder(messages.length, c.placeholder)}
            className="min-h-10 max-h-40 resize-none text-sm"
            rows={1}
            disabled={isWorking}
          />

          {/* Send / Stop button */}
          {isWorking ? (
            <Button
              type="button"
              size="icon-sm"
              variant="outline"
              onClick={() => stop()}
              className="shrink-0 active:scale-95 transition-transform"
              aria-label="Stop generation"
            >
              <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5">
                <rect x="3" y="3" width="10" height="10" rx="1.5" />
              </svg>
            </Button>
          ) : (
            <Button
              type="submit"
              size="icon-sm"
              disabled={!inputValue.trim() && !imagePreview}
              className="shrink-0 active:scale-95 transition-transform"
              aria-label="Send message"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                <path d="M3.105 2.288a.75.75 0 0 0-.826.95l1.414 4.926A1.5 1.5 0 0 0 5.135 9.25h6.115a.75.75 0 0 1 0 1.5H5.135a1.5 1.5 0 0 0-1.442 1.086l-1.414 4.926a.75.75 0 0 0 .826.95 28.897 28.897 0 0 0 15.293-7.154.75.75 0 0 0 0-1.114A28.897 28.897 0 0 0 3.105 2.288Z" />
              </svg>
            </Button>
          )}
        </form>

        {/* Keyboard shortcut hint */}
        <div className="mt-1.5 hidden sm:flex items-center justify-end gap-3 text-[10px] text-muted-foreground/40">
          <span><kbd className="px-1 py-0.5 rounded border border-border/50 text-[9px] font-mono">Enter</kbd> send</span>
          <span><kbd className="px-1 py-0.5 rounded border border-border/50 text-[9px] font-mono">Shift+Enter</kbd> newline</span>
        </div>
      </div>
    </div>
  );
}
